import { OpenAPI } from "../client"

const getAuthorizedHeaders = () => {
  const token = localStorage.getItem("access_token") || ""
  return token ? { Authorization: `Bearer ${token}` } : undefined
}

export const extractFileName = (
  contentDisposition: string | null,
  fallbackFileName: string,
) => {
  const match = contentDisposition?.match(/filename="?([^"]+)"?/)
  const value = match?.[1]?.trim()
  return value || fallbackFileName
}

export const fetchProtectedFile = async (
  path: string,
  fallbackFileName: string,
  init?: RequestInit,
) => {
  const response = await fetch(`${OpenAPI.BASE}${path}`, {
    ...init,
    method: init?.method ?? "GET",
    headers: {
      ...(init?.headers ?? {}),
      ...(getAuthorizedHeaders() ?? {}),
    },
  })

  if (!response.ok) {
    let message = `HTTP ${response.status}`
    try {
      const body = await response.json()
      message = body?.error ?? message
    } catch {
      // Keep generic HTTP status when body is not JSON.
    }
    throw new Error(message)
  }

  const blob = await response.blob()
  const fileName = extractFileName(
    response.headers.get("content-disposition"),
    fallbackFileName,
  )
  return { blob, fileName, response }
}

export const downloadProtectedFile = async (
  path: string,
  fallbackFileName: string,
  init?: RequestInit,
) => {
  const { blob, fileName } = await fetchProtectedFile(
    path,
    fallbackFileName,
    init,
  )
  const objectUrl = window.URL.createObjectURL(blob)

  const link = document.createElement("a")
  link.href = objectUrl
  link.download = fileName
  document.body.appendChild(link)
  link.click()
  link.remove()

  window.setTimeout(() => {
    window.URL.revokeObjectURL(objectUrl)
  }, 60_000)
}

export const createProtectedFileObjectUrl = async (
  path: string,
  fallbackFileName: string,
  init?: RequestInit,
) => {
  const { blob, fileName } = await fetchProtectedFile(
    path,
    fallbackFileName,
    init,
  )
  const objectUrl = window.URL.createObjectURL(blob)
  return { objectUrl, fileName }
}

export const fetchProtectedFileAsDataUrl = async (
  path: string,
  fallbackFileName = "file",
  init?: RequestInit,
) => {
  const { blob } = await fetchProtectedFile(path, fallbackFileName, init)
  return await new Promise<string>((resolve, reject) => {
    const reader = new FileReader()
    reader.onloadend = () => resolve(String(reader.result ?? ""))
    reader.onerror = () => reject(new Error("Failed to read file"))
    reader.readAsDataURL(blob)
  })
}
